import Container = PIXI.Container;
import Sprite = PIXI.Sprite;
import TextStyle = PIXI.TextStyle;
import { PopUpManager, PopUpName } from '../core/managers/PopUpManager';
import Point = PIXI.Point;
import { SceneManager } from '../core/managers/SceneManager';
import Graphics = PIXI.Graphics;
import { createSprite, resizeTextByReference, setScaleByReference } from '../utils/GameEngine';
import { TextStyles } from '../utils/TextStyles';
import { CloseButton } from '../UI/elements/buttons/CloseButton';
import { removeAllSignals } from '../utils/EventUtils';

export interface AbstractPopUpParams {
    noOverlay?: boolean;
    text?: string;
    text2?: string;
    image?: string;
    noDefaultAnimations?: boolean;
    animationDuration?: number;
    noCloseButton?: boolean;
    noBackground?: boolean;
    onCloseCallback?: () => any;
    customBackground?: string;
}

export class AbstractPopUp extends Container {
    protected animationIn: () => void;
    protected animationOut: (callback: () => void) => void;
    protected animationDuration: number = 0.3;
    protected background: PIXI.Container;
    protected closeButton: CloseButton;
    protected image: PIXI.Sprite;
    protected text: PIXI.Text;
    protected text2: PIXI.Text;
    protected buttonY: number = 450;
    protected imageBox: Graphics;
    protected textBox: Graphics;
    protected bgSprite: PIXI.Sprite;
    private originalScale: Point;
    private onCloseCallback: () => any;

    constructor(public readonly name: PopUpName, public readonly params?: AbstractPopUpParams) {
        super();
        this.drawLayout(params);
        this.setPosition();
        if (!params.noDefaultAnimations) {
            this.animationIn = this.defaultAnimationIn;
            this.animationOut = this.defaultAnimationOut;
        }
        if (params.animationDuration) {
            this.animationDuration = params.animationDuration;
        }
        if (params.onCloseCallback) {
            this.onCloseCallback = params.onCloseCallback;
        }
    }

    public onRegister(): void {
        //
    }

    protected drawLayout(params?: any): void {
        this.drawBackground();
        if (params && params.title) {
            this.drawTitle(params.title);
        }
        if (params && !params.noCloseButton) {
            this.addCloseButton();
        }
        // this.background.cacheAsBitmap = true;
    }

    protected addCloseButton(): void {
        let buttonX: number = 540;
        this.closeButton = new CloseButton(this.onClose.bind(this), this);
        if (this.background) {
            buttonX = this.background.width - 54 / 2;
        }
        this.closeButton.position.set(buttonX, 32);
    }

    protected drawTitle(title: string): void {
        const titleBackground: Sprite = createSprite('blue_ribbon_new', this);
        titleBackground.anchor.set(0.5, 0.5);
        titleBackground.position.set(this.background.width / 2, this.background.x);
        const style: TextStyle = TextStyles.uiText.clone();
        style.wordWrap = false;
        const label: PIXI.Text = new PIXI.Text(title, style);
        const titleArea: Graphics = new Graphics();
        titleArea.lineStyle(0);
        titleArea.beginFill(0xFF0000);
        titleArea.drawRect(0, 0, 210, 50);
        titleArea.endFill();
        titleArea.position.set(160, -30);
        this.addChild(label);
        resizeTextByReference(label, titleArea);
        label.anchor.set(0.5, 0.5);
        label.position.set(titleBackground.x, titleBackground.y - 10);

    }

    protected drawBackground(): void {
        const sideOffset: number = 48;
        const topOffset: number = 16;
        this.background = new Container();
        this.addChild(this.background);
        this.bgSprite = createSprite(this.params.customBackground || 'win_popup_background', this.background);
        if (this.params.noBackground) {
            this.bgSprite.visible = false;
        }
        let imageBoxWidth: number = 300;
        let imageBoxHeight: number = 300;
        if (this.bgSprite) {
            imageBoxWidth = Math.round(this.bgSprite.width / 3);
            imageBoxHeight = this.bgSprite.height;
        }
        this.imageBox = new Graphics();
        this.imageBox.lineStyle(0);
        this.imageBox.beginFill(0x000000);
        this.imageBox.drawRect(0, 0, imageBoxWidth, imageBoxHeight);
        this.imageBox.position.set(0, 0);
        this.imageBox.endFill();
        this.imageBox.x += sideOffset;
        // this.background.addChild(this.imageBox);
        if (this.params.image) {
            this.image = createSprite(this.params.image, this.background);
            this.image.anchor.set(0.5, 0.5);
            setScaleByReference(this.image, this.imageBox, true);
            this.image.x = this.imageBox.x + this.imageBox.width / 2;
            this.image.y = this.imageBox.y + this.imageBox.height / 2 + topOffset;
        }
        const offsetBetween: number = 24;
        this.textBox = new Graphics();
        this.textBox.lineStyle(0);
        this.textBox.beginFill(0xff0000);
        let textBoxWidth: number = 300;
        let textBoxHeigth: number = 300;
        if (this.bgSprite) {
            textBoxWidth = this.bgSprite.width - imageBoxWidth - sideOffset - offsetBetween - sideOffset;
            textBoxHeigth = this.bgSprite.height;
        }
        this.textBox.drawRect(0, 0, textBoxWidth, textBoxHeigth);
        this.textBox.position.set(0, 0);
        this.textBox.endFill();
        this.textBox.x += this.imageBox.x + this.imageBox.width + offsetBetween;
        // this.background.addChild(this.textBox);
        if (this.params.text) {
            this.addFirstText(topOffset);
        }
        if (this.params.text2) {
            // TODO: CREATE NEW STYLE
            this.addSecondText(topOffset);
        }
        this.background.cacheAsBitmap = true;
    }

    protected addFirstText(topOffset: number): void {
        const style: TextStyle = TextStyles.uiText.clone();
        style.wordWrapWidth = this.textBox.width - 16;
        this.text = new PIXI.Text(this.params.text, style);
        resizeTextByReference(this.text, this.textBox, 0);
        this.text.anchor.set(0.5, 0.5);
        style.wordWrapWidth = this.textBox.width - 16;
        this.text.position.set(
            this.textBox.x + this.textBox.width / 2,
            this.textBox.y + topOffset * 3
        );
        this.background.addChild(this.text);
    }

    protected addSecondText(topOffset: number): void {
        const style: TextStyle = TextStyles.smallTextWhite.clone();
        style.wordWrapWidth = this.textBox.width - 16;
        style.wordWrap = true;
        this.text2 = new PIXI.Text(this.params.text2, style);
        this.text2.anchor.set(0.5, 0.5);
        let textY: number = this.textBox.y + this.textBox.height / 2;
        if (this.text) {
            textY = this.text.y + this.text.height / 2 + this.text2.height / 2 + topOffset * 2;
        }
        resizeTextByReference(this.text2, this.textBox, 0);
        this.text2.position.set(
            this.textBox.x + this.textBox.width / 2,
            textY
        );
        this.background.addChild(this.text2);
    }

    protected setPosition(): void {
        this.scale.set(0.9, 0.9);
        this.pivot = new Point(this.width / 2, this.height / 2);
        this.position.set(SceneManager.gameWidth / 2, SceneManager.gameHeight / 2 - 100);
    }

    public show(keepQueue: boolean = false): void {
        if (this.params.onCloseCallback) {
            this.onCloseCallback = this.params.onCloseCallback;
        }
        PopUpManager.getInstance().show(this.name, keepQueue);
        if (this.animationIn) {
            this.animationIn();
        }
    }

    public hide(quick: boolean = false, ignoreCallback: boolean = false): void {
        if (this.onCloseCallback && !ignoreCallback) {
            this.onCloseCallback();
            this.onCloseCallback = null;
        }
        if (this.animationOut && !quick) {
            this.animationOut(() => {
                PopUpManager.getInstance().hide(this.name);
            });
        } else PopUpManager.getInstance().hide(this.name);
    }

    protected defaultAnimationIn(): void {
        if (!this.originalScale) {
            this.originalScale = new Point(this.scale.x, this.scale.y);
        }
        this.scale = new Point(0, 0);
        if (!this.params.noOverlay) {
            PopUpManager.getInstance().animateOverlayIn(this.animationDuration);
        }
        TweenLite.to(this.scale, this.animationDuration, {
            ease: Back.easeOut,
            x: this.originalScale.x,
            y: this.originalScale.y
        });
    }

    protected defaultAnimationOut(callback: () => void): void {
        if (this.scale) {
            TweenLite.to(this.scale, this.animationDuration, {
                x: 0,
                y: 0,
                ease: Back.easeIn,
                onComplete: callback.bind(this)
            });
            if (!this.params.noOverlay) {
                PopUpManager.getInstance().animateOverlayOut(this.animationDuration);
            }
        }
    }

    protected onClose(): void {
        this.hide();
    }

    public destroy(options?: any): void {
        removeAllSignals(this);
        super.destroy(options);
    }
}